import { useReducer, useState } from "react";
import {
  checklistSaveInputSchema,
  checklistMaterialInputSchema,
  type ChecklistMaterial,
  type ChecklistSaveInput,
  type MaterialChecklist,
} from "cadendar-shared";
import { trpc } from "../main/components/trpc";
import { checklistEditorReducer } from "./checklistEditor";
import { MaterialPicker } from "./MaterialPicker";

const buttonClass =
  "rounded border border-slate-300 bg-white px-3 py-2 disabled:opacity-50";

function MaterialLibraryRow({ material }: { material: ChecklistMaterial }) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(material.label);
  const [error, setError] = useState("");
  const save = trpc.checklist.saveMaterial.useMutation({
    onSuccess: async () => {
      await utils.checklist.invalidate();
      setEditing(false);
    },
  });
  const remove = trpc.checklist.deleteMaterial.useMutation({
    onSuccess: () => utils.checklist.invalidate(),
  });
  const busy = save.isPending || remove.isPending;
  return (
    <li className="space-y-2 border-b py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-medium">{material.label}</span>
          {material.catalogProductId && (
            <small className="block text-slate-500">
              {material.catalogBrand} · {material.catalogName}
              {material.catalogReference &&
                ` · Réf. ${material.catalogReference}`}
            </small>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className={buttonClass}
            disabled={busy}
            onClick={() => {
              setLabel(material.label);
              setEditing(!editing);
            }}
          >
            Renommer
          </button>
          <button
            type="button"
            className={buttonClass}
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  `Supprimer « ${material.label} » du matériel réutilisable ? Cela n’est possible que s’il n’est utilisé dans aucune checklist.`,
                )
              )
                remove.mutate({ id: material.id });
            }}
          >
            Supprimer
          </button>
        </div>
      </div>
      {editing && (
        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const parsed = checklistMaterialInputSchema.safeParse({
              id: material.id,
              label,
              catalogProductId: material.catalogProductId,
            });
            if (!parsed.success) {
              setError(parsed.error.issues[0]?.message ?? "Libellé invalide.");
              return;
            }
            setError("");
            save.mutate(parsed.data);
          }}
        >
          <label className="flex-1">
            Nouveau libellé
            <input
              className="ml-2 rounded border p-2"
              required
              maxLength={300}
              disabled={busy}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
          <button className={buttonClass} disabled={busy}>
            Enregistrer
          </button>
        </form>
      )}
      {(error || save.error || remove.error) && (
        <p role="alert" className="text-red-700">
          {error || save.error?.message || remove.error?.message}
        </p>
      )}
    </li>
  );
}

function ChecklistEditor({
  initial,
  materials,
  onClose,
}: {
  initial: ChecklistSaveInput;
  materials: ChecklistMaterial[];
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const [draft, dispatch] = useReducer(checklistEditorReducer, initial);
  const [error, setError] = useState("");
  const save = trpc.checklist.save.useMutation({
    onSuccess: async () => {
      await utils.checklist.invalidate();
      onClose();
    },
  });
  const dirty = JSON.stringify(initial) !== JSON.stringify(draft);
  return (
    <section className="space-y-4 rounded border bg-white p-5">
      <h2 className="text-xl font-semibold">
        {draft.id ? "Modifier la checklist" : "Nouvelle checklist"}
      </h2>
      <form
        id="checklist-form"
        onSubmit={(event) => {
          event.preventDefault();
          const parsed = checklistSaveInputSchema.safeParse(draft);
          if (!parsed.success) {
            setError(parsed.error.issues[0]?.message ?? "Checklist invalide.");
            return;
          }
          setError("");
          save.mutate(parsed.data);
        }}
      >
        <label className="block">
          Nom de la checklist
          <input
            className="mt-1 w-full rounded border p-2"
            required
            maxLength={150}
            value={draft.name}
            disabled={save.isPending}
            onChange={(event) =>
              dispatch({ type: "name", name: event.target.value })
            }
          />
        </label>
      </form>
      <ol aria-label="Lignes de la checklist" className="space-y-2">
        {draft.materialIds.map((id, index) => {
          const material = materials.find((entry) => entry.id === id);
          return (
            <li
              key={id}
              className="flex flex-wrap items-center justify-between gap-2 rounded border p-2"
            >
              <span>
                {index + 1}. {material?.label ?? "Matériel indisponible"}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  className={buttonClass}
                  aria-label={`Monter ${material?.label ?? "le matériel"}`}
                  disabled={save.isPending || index === 0}
                  onClick={() => dispatch({ type: "move", id, direction: -1 })}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className={buttonClass}
                  aria-label={`Descendre ${material?.label ?? "le matériel"}`}
                  disabled={
                    save.isPending || index === draft.materialIds.length - 1
                  }
                  onClick={() => dispatch({ type: "move", id, direction: 1 })}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className={buttonClass}
                  disabled={save.isPending}
                  onClick={() => dispatch({ type: "remove", id })}
                >
                  Retirer
                </button>
              </div>
            </li>
          );
        })}
      </ol>
      {!draft.materialIds.length && (
        <p className="text-slate-500">
          Ajoutez le matériel à sortir, dans l’ordre souhaité.
        </p>
      )}
      <MaterialPicker
        materials={materials}
        selectedIds={draft.materialIds}
        disabled={save.isPending}
        onAdd={(id) => dispatch({ type: "add", id })}
      />
      {(error || save.error) && (
        <p role="alert" className="text-red-700">
          {error || save.error?.message}
        </p>
      )}
      <div className="flex gap-2">
        <button
          form="checklist-form"
          className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
          disabled={save.isPending}
        >
          {save.isPending ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button
          type="button"
          className={buttonClass}
          disabled={save.isPending}
          onClick={() => {
            if (
              !dirty ||
              window.confirm(
                "Abandonner les modifications de cette checklist ?",
              )
            )
              onClose();
          }}
        >
          Annuler
        </button>
      </div>
    </section>
  );
}

export default function ChecklistsPage() {
  const utils = trpc.useUtils();
  const query = trpc.checklist.list.useQuery();
  const [editor, setEditor] = useState<{
    key: string;
    initial: ChecklistSaveInput;
  } | null>(null);
  const remove = trpc.checklist.delete.useMutation({
    onSuccess: () => utils.checklist.invalidate(),
  });
  const startEditor = (checklist?: MaterialChecklist, duplicate = false) =>
    setEditor({
      key: crypto.randomUUID(),
      initial: {
        id: duplicate ? null : (checklist?.id ?? null),
        expectedRevision: duplicate ? 0 : (checklist?.revision ?? 0),
        name: checklist
          ? `${checklist.name}${duplicate ? " (copie)" : ""}`
          : "",
        materialIds: checklist?.items.map((item) => item.id) ?? [],
      },
    });
  return (
    <main className="mx-auto w-full max-w-5xl space-y-5 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Checklists de matériel</h1>
        <p className="mt-1 text-slate-600">
          Modèles et matériel réutilisable communs à votre organisation. La
          préparation se coche sur chaque téléphone.
        </p>
      </header>
      {query.isPending && <p role="status">Chargement…</p>}
      {query.error && (
        <div role="alert" className="text-red-700">
          Impossible de charger les checklists.{" "}
          <button className="underline" onClick={() => void query.refetch()}>
            Réessayer
          </button>
        </div>
      )}
      {query.data && (
        <>
          {editor ? (
            <ChecklistEditor
              key={`${query.data.organizationId}:${editor.key}`}
              initial={editor.initial}
              materials={query.data.materials}
              onClose={() => setEditor(null)}
            />
          ) : (
            <>
              <button
                className="rounded bg-blue-600 px-4 py-2 text-white"
                onClick={() => startEditor()}
              >
                Nouvelle checklist
              </button>
              <ul className="space-y-3" aria-label="Checklists du cabinet">
                {query.data.checklists.map((checklist) => (
                  <li
                    key={checklist.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded border bg-white p-4"
                  >
                    <div>
                      <h2 className="font-semibold">{checklist.name}</h2>
                      <p className="text-sm text-slate-500">
                        {checklist.items.length} élément
                        {checklist.items.length > 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className={buttonClass}
                        onClick={() => startEditor(checklist)}
                      >
                        Modifier
                      </button>
                      <button
                        className={buttonClass}
                        onClick={() => startEditor(checklist, true)}
                      >
                        Dupliquer
                      </button>
                      <button
                        className={buttonClass}
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Supprimer la checklist « ${checklist.name} » ? Le matériel restera réutilisable.`,
                            )
                          )
                            remove.mutate({ id: checklist.id });
                        }}
                      >
                        Supprimer
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              {!query.data.checklists.length && (
                <p>
                  Aucune checklist. Créez votre premier modèle de préparation.
                </p>
              )}
              {remove.error && (
                <p role="alert" className="text-red-700">
                  {remove.error.message}
                </p>
              )}
              <details className="rounded border bg-white p-4">
                <summary className="cursor-pointer font-semibold">
                  Matériel réutilisable du cabinet (
                  {query.data.materials.length})
                </summary>
                <p className="mt-2 text-sm text-slate-500">
                  Un renommage s’applique à toutes les checklists qui utilisent
                  ce matériel.
                </p>
                <ul>
                  {query.data.materials.map((material) => (
                    <MaterialLibraryRow key={material.id} material={material} />
                  ))}
                </ul>
              </details>
            </>
          )}
        </>
      )}
    </main>
  );
}
